/**
 * # The one cell store, as a row's `RowCellCache` reads and writes it
 *
 * A row a sheet holds keeps its cells in ONE store: the sheet's rows-side
 * cell store (`sheetWiring(sheet).localCells`, keyed by `RowLocalId`) or —
 * for a canonical key (`:~…`) — the client's `canonicalCells` (keyed by the
 * key). The row's `RowCellCache` is a VIEW over that store
 * (`CellStoreView`), not a second copy of it:
 *
 * - every read answers the store's cells (`dates`, `timepoints`);
 * - every local write is a store write (`set`, `clear`, `setTimepoint`,
 *   `removeTimepoint`, `replaceTimepoints`, `writeAnswer`), stamped with the
 *   writer's actor;
 * - a server read the cache's own fetch made is installed into the store
 *   (`install`) as a snapshot — the store skips a cell it already holds
 *   exactly, and with `since` a cell written locally after the read began is
 *   left as it is;
 * - the store's change feed is what fires the cache's listeners
 *   (`CellStoreView.link`), carrying the change's actor, so a renderer's
 *   self-echo check (`change.actor === cellActor`) recognises its own write,
 *   and a burst inside one store `batch` costs one fire per touched cache.
 *
 * No DOM, no `Sheet`: this module sees a store and a key.
 */

import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, type Utc } from "@tento-chrono";
import { ListenerErrors, type ListenerErrorSink, type Thrown } from "@tento-core/listener-errors";
import { type CellEntry, CellId, type CellValue, type Json, type RowNetworkKey, type Unsubscribe } from "@tento-lona/sheets";
import { type CellCardinality, ComputedDataSource, isCanonicalKey, type Sheet, SourceRow } from "@tento-lona/sheets/platform";
import type { RowLocalId } from "@tento-lona/sheets";
import {
  type CellDataBatchHandler,
  type CellDataChange,
  type CellDataChangeHandler,
  PartialDateMemo,
  UtcDays,
} from "@tento-lona/sheets/internal-sdk";
import type { Actor } from "../invalidation.ts";
import type { RowData } from "../sync/row-data.ts";
import type { StoreChange } from "../sync/cell-authority.ts";

/** A timepoint (custom) cell as the store keeps it. */
type Timepoint = CellEntry.Timepoint;

/**
 * The store surface a view needs, keyed by `K` — both rows-side cell stores
 * (`SourceRowCellData` per sheet, `CellDataStore<string>` per client) have it.
 */
export interface CellStorePort<K> {
  /** SDK projection composition; absent for direct stores and legacy fixtures. */
  readonly ordinaryStore?: (key: K) => Option<CellStorePort<K>>;
  cells(key: K, range: Option<NaiveDate.Range>): readonly CellEntry[];
  customCells(key: K, range: Option<DateTime.Range<Utc>>): readonly Timepoint[];
  /** The key's date-keyed and timepoint cells, counted without listing them. */
  cellCount(key: K): number;
  setCardinality(key: K, cardinality: CellCardinality): void;
  /** Physical declarations precede resident creation; provisional cache defaults cannot replace them. */
  declaredCardinality(key: K): Option<CellCardinality>;
  /** Keyed resident admission; pure rowData probes remain nonallocating. */
  captureRead(key: K): {
    readonly revision: number;
    readonly resident: object;
    readonly current: () => boolean;
    readonly release: Unsubscribe;
  };
  /** The key's per-row store, `null` before its first cell (its cardinality is declared then). */
  rowData(key: K): Option<object>;
  set(key: K, date: NaiveDate.Partial, value: CellValue, cellId: Option<CellId>, style: null, actor: Option<Actor>): void;
  clear(key: K, date: NaiveDate.Partial, cellId: Option<CellId>, actor: Option<Actor>): void;
  setCustom(key: K, entry: Timepoint, actor: Option<Actor>): void;
  removeCustom(key: K, id: string, actor: Option<Actor>): void;
  replaceCustomInRange(key: K, range: DateTime.Range<Utc>, entries: readonly Timepoint[], actor: Option<Actor>): void;
  batch<T>(fn: () => T): T;
  asInstall<T>(fn: () => T): T;
  writtenLocallySince(key: K, since: number, date: NaiveDate.Partial, cellId: Option<CellId>): boolean;
  customWrittenLocallySince(key: K, since: number, id: string): boolean;
  /** Register an outstanding read at the current local-write revision; pair with `releaseRevision`. */
  captureRevision(): number;
  /** The read captured at `rev` installed or bailed. */
  releaseRevision(rev: number): void;
}

/** The change feed of a store (`CellStoreView.link`). */
export interface CellStoreFeed<K> {
  subscribeAll(handler: CellDataChangeHandler<K>): Unsubscribe;
  subscribeBatch(handler: CellDataBatchHandler): Unsubscribe;
  /** True while the store is inside a server-snapshot install bracket (`asInstall`). */
  readonly isInstalling: boolean;
  /** Composite feeds report the changed key's physical lane; direct feeds retain isInstalling. */
  readonly isInstallingFor?: (key: K) => boolean;
}

/** One cell of a server read or a write's answer, as a row cache decodes it. */
export type WireCell = RowData.Cell;

export type { StoreChange } from "../sync/cell-authority.ts";

/** What a view link resolves a change to: the cache that shows the changed key. */
export interface StoreChangeTarget {
  /** Suspend listener fires until the release runs (re-entrant). */
  deferNotifications(): () => void;
  /** The store changed a cell this cache shows. */
  storeChanged(change: StoreChange): void;
}

/** The read half of a view. */
type Reads = {
  readonly dates: (range: Option<NaiveDate.Range>) => readonly CellEntry[];
  readonly timepoints: (range: Option<DateTime.Range<Utc>>) => readonly Timepoint[];
  readonly count: () => number;
};

/**
 * One row's cells in the one store: a key of a store, read and written
 * through it (see the module doc).
 */
export class CellStoreView {
  readonly #reads: Reads;
  readonly #writes: CellStoreView.Writes;
  /** Where the row's computed cells come from (a computed or formula row's own load); `null`: a stored row. */
  readonly compute: Option<(range: NaiveDate.Range) => Promise<void>>;
  /** Which store: the holding sheet's (`local`) or the client's canonical one. */
  readonly store: "local" | "canonical";
  #sheetScoped = false;
  #clientScoped = false;
  /** A client-owned stored authority remains installed when its last sheet releases. */
  get clientScoped(): boolean { return this.#clientScoped; }
  /** Mark a composed view as the stable client-owned authority. */
  retainAuthority(): this { this.#clientScoped = true; return this; }

  /** Derived values belong to this sheet context, even when their row key looks canonical. */
  get sheetScoped(): boolean {
    return this.#sheetScoped;
  }

  private constructor(
    store: "local" | "canonical",
    reads: Reads,
    writes: CellStoreView.Writes,
    compute: Option<(range: NaiveDate.Range) => Promise<void>>,
  ) {
    this.store = store;
    this.#reads = reads;
    this.#writes = writes;
    this.compute = compute;
  }

  /**
   * The view of key `key` of `port`. `cardinality` answers the row's cell
   * cardinality as of each write (a multi-cardinality store keys its cells
   * by id); `compute` the row's computed load (`null`: a stored row).
   */
  static of<K>(
    store: "local" | "canonical",
    port: CellStorePort<K>,
    key: K,
    cardinality: () => CellCardinality,
    compute: Option<(range: NaiveDate.Range) => Promise<void>>,
  ): CellStoreView {
    const dates = new PartialDateMemo(PartialDateMemo.CAPACITY);
    // A key's cardinality is declared before its first cell; the store refuses
    // to re-declare a key that holds cells.
    const settle = (): CellCardinality => {
      const c = port.declaredCardinality(key) ?? cardinality();
      if (port.rowData(key) == null) port.setCardinality(key, c);
      return c;
    };
    const writes: CellStoreView.Writes = {
      set: (date, value, id, actor) => {
        settle();
        if (value == null) port.clear(key, date, id, actor);
        else port.set(key, date, value, id, null, actor);
      },
      clear: (date, id, actor) => {
        settle();
        port.clear(key, date, id, actor);
      },
      setTimepoint: (entry, actor) => port.setCustom(key, entry, actor),
      removeTimepoint: (id, actor) => port.removeCustom(key, id, actor),
      replaceTimepoints: (range, entries, actor) => port.replaceCustomInRange(key, range, entries, actor),
      batch: (fn) => port.batch(fn),
      install: (cells, since) => {
        if (cells.length === 0) return;
        const c = settle();
        port.asInstall(() =>
          port.batch(() => {
            for (const cell of cells) writeOne(port, key, cell, since, dates, c, null);
          })
        );
      },
      adopt: (dated, timepoints) => {
        if (dated.length === 0 && timepoints.length === 0) return;
        const c = settle();
        port.asInstall(() =>
          port.batch(() => {
            for (const cell of dated) {
              if (c === "multi" && cell.id == null) continue;
              const held = port.cells(key, cell.date.toRange()).some((h) =>
                h.date.type === cell.date.type && h.date.index === cell.date.index && (c !== "multi" || h.id === cell.id)
              );
              if (!held) port.set(key, cell.date, cell.value, cell.id ?? null, null, null);
            }
            const ids = new Set(port.customCells(key, null).map((h) => h.id));
            for (const entry of timepoints) if (!ids.has(entry.id)) port.setCustom(key, entry, null);
          })
        );
      },
      captureRead: () => {
        settle();
        return port.captureRead(key);
      },
      captureRevision: () => port.captureRevision(),
      releaseRevision: (rev) => port.releaseRevision(rev),
      writeAnswer: (cells, actor) => {
        if (cells.length === 0) return;
        const c = settle();
        port.batch(() => {
          for (const cell of cells) writeOne(port, key, cell, null, dates, c, actor);
        });
      },
    };
    return new CellStoreView(store, {
      dates: (range) => port.cells(key, range),
      timepoints: (range) => port.customCells(key, range),
      count: () => port.cellCount(key),
    }, writes, compute);
  }

  /**
   * The view of the row `accessKey` names in `sheet`: a canonical key reads
   * the client's canonical store (`canonical`, keyed by the key); any other
   * key the sheet's own cell store (`local`), keyed by the row's local id —
   * when the sheet holds that row as a source row (`null` otherwise: the
   * sheet keeps no cells for it). The row's cardinality is its source
   * descriptor's (any source row the sheet holds under a canonical key),
   * else `fallback`'s; its computed load is the row's own (`ensureLoaded`).
   */
  static ofSheetRow(
    sheet: Sheet,
    local: CellStorePort<RowLocalId>,
    canonical: CellStorePort<string>,
    accessKey: RowNetworkKey,
    localId: Option<RowLocalId>,
    fallback: () => CellCardinality,
  ): Option<CellStoreView> {
    const sourceOf = (): Option<SourceRow> => {
      const row = localId == null ? null : sheet.peekRow(localId);
      if (row instanceof SourceRow) return row;
      if (!isCanonicalKey(accessKey)) return null;
      for (const r of sheet.peekAllByLookupKey(accessKey)) if (r instanceof SourceRow) return r;
      return null;
    };
    const cardinality = (): CellCardinality => {
      const row = sourceOf();
      if (row == null) return fallback();
      return row.descriptor.cellCardinality === "multi" ? "multi" : "single";
    };
    // A computed row's cells for `range` are the rows side's recompute of
    // it (the sheet's own sweep covers only the days its cells observe).
    const compute = async (range: NaiveDate.Range): Promise<void> => {
      const row = sourceOf();
      if (row == null) return;
      await row.ensureLoaded(range, null);
      if (row instanceof ComputedDataSource) row.recompute(range);
    };
    const source = sourceOf();
    if (source != null && source.cellsDerived) {
      const view = CellStoreView.of("local", local, source.id, cardinality, compute);
      view.#sheetScoped = true;
      return view;
    }
    if (isCanonicalKey(accessKey)) return CellStoreView.of("canonical", canonical, accessKey, cardinality, compute).retainAuthority();
    if (localId == null || !(sheet.peekRow(localId) instanceof SourceRow)) return null;
    const ordinary = local.ordinaryStore?.(localId) ?? null;
    return ordinary == null ? CellStoreView.of("local", local, localId, cardinality, compute)
      : CellStoreView.of("local", ordinary, localId, cardinality, null).retainAuthority();
  }

  /** The row's date-keyed cells in `range` (`null`: every one), as the store holds them. */
  dates(range: Option<NaiveDate.Range>): readonly CellEntry[] {
    return this.#reads.dates(range);
  }

  /** The row's timepoint cells starting in `range` (`null`: every one). */
  timepoints(range: Option<DateTime.Range<Utc>>): readonly Timepoint[] {
    return this.#reads.timepoints(range);
  }

  /** The row's date-keyed and timepoint cells in the store, counted without listing them. */
  cellCount(): number {
    return this.#reads.count();
  }

  /** Write `value` into the date-keyed cell (`null`: clear it), stamped with `actor`. */
  set(date: NaiveDate.Partial, value: Option<Json | CellValue>, id: Option<string>, actor: Option<Actor>): void {
    this.#writes.set(date, value ?? null, idOf(id), actor);
  }

  /** Clear the date-keyed cell (`id`: a multi-cardinality row's cell). */
  clear(date: NaiveDate.Partial, id: Option<string>, actor: Option<Actor>): void {
    this.#writes.clear(date, idOf(id), actor);
  }

  /** Upsert one timepoint cell. */
  setTimepoint(entry: Timepoint, actor: Option<Actor>): void {
    this.#writes.setTimepoint(entry, actor);
  }

  /** Remove one timepoint cell by id. */
  removeTimepoint(id: string, actor: Option<Actor>): void {
    this.#writes.removeTimepoint(id, actor);
  }

  /** Replace every timepoint cell starting in `range` with `entries`. */
  replaceTimepoints(range: DateTime.Range<Utc>, entries: readonly Timepoint[], actor: Option<Actor>): void {
    this.#writes.replaceTimepoints(range, entries, actor);
  }

  /** Run `fn`'s writes as one store burst (one listener fire per touched cache). */
  batch<T>(fn: () => T): T {
    return this.#writes.batch(fn);
  }

  /**
   * Install the (decoded) cells of a server read as a snapshot: the store
   * skips a cell it holds exactly; with `since`, a cell written locally after
   * revision `since` is left as it is.
   */
  install(cells: readonly WireCell[], since: Option<number>): void {
    this.#writes.install(cells, since);
  }

  /** Admit a read against this view's exact physical resident and marker clock. */
  captureRead(): {
    readonly revision: number;
    readonly resident: object;
    readonly current: () => boolean;
    readonly release: Unsubscribe;
  } {
    return this.#writes.captureRead();
  }

  /**
   * The store's local-write revision as a read goes out (its answer installs
   * with it as `since`); pair with `releaseRevision` once the answer installed
   * or the read bailed.
   */
  captureRevision(): number {
    return this.#writes.captureRevision();
  }

  /** The read captured at `rev` installed or bailed (see `captureRevision`). */
  releaseRevision(rev: number): void {
    this.#writes.releaseRevision(rev);
  }

  /**
   * The cells a row held itself before a sheet held it (its own copy) join
   * the store as a snapshot — each cell the store does not hold yet; the
   * store's cells win.
   */
  adopt(dates: readonly CellEntry[], timepoints: readonly Timepoint[]): void {
    this.#writes.adopt(dates, timepoints);
  }

  /** Write the cells a write's answer carries (decoded), stamped with the writer's `actor`. */
  writeAnswer(cells: readonly WireCell[], actor: Option<Actor>): void {
    this.#writes.writeAnswer(cells, actor);
  }

  /** The timepoint cell a wire cell is (`null`: a date-keyed cell, or one without an id). */
  timepointOf(cell: WireCell): Option<Timepoint> {
    return timepointOf(cell);
  }

  /**
   * Fire `resolve`'s cache for every change of `feed`, and collapse each store
   * `batch` bracket into one fire per touched cache (the cache defers its
   * fires while the bracket is open). `bridgeActor` stands for an untagged
   * local change (a formula compute, a sync replay): a singleton no plugin
   * holds, so a self-echo check never matches it. A change made by
   * `serverActor` (a server read's ingest) or inside an install bracket is a
   * server value. Returns the teardown, which also closes any open bracket.
   *
   * Each cache is a subscriber of the store, never part of the store write
   * that notified it: a cache whose notification (its index rebuild, its
   * fan-out) throws — at a change, or when a bracket's deferred fires are
   * released — does not stop the other caches nor fail the write; what they
   * threw goes to `listenerErrors` (`ListenerErrors.report`) once all ran.
   */
  static link<K>(
    feed: CellStoreFeed<K>,
    resolve: (key: K, change: StoreChange) => Option<StoreChangeTarget>,
    bridgeActor: Actor,
    serverActor: Option<Actor>,
    listenerErrors: ListenerErrorSink = ListenerErrors.RETHROW,
  ): Unsubscribe {
    const deferred = new Map<StoreChangeTarget, () => void>();
    let batching = false;
    const releaseAll = (): void => {
      batching = false;
      const releases = [...deferred.values()];
      deferred.clear();
      ListenerErrors.report(
        listenerErrors,
        ListenerErrors.fanOut(releases, () => true, (release) => release()),
        "CellStoreView.link: notification releases",
      );
    };
    const unsubBatch = feed.subscribeBatch({
      begin: () => {
        batching = true;
      },
      end: releaseAll,
    });
    const unsubChanges = feed.subscribeAll((change) => {
      const installing = feed.isInstallingFor == null ? feed.isInstalling : feed.isInstallingFor(change.key);
      const seen = changeOf(change, installing ? "install" : "write", bridgeActor, serverActor);
      const target = resolve(change.key, seen);
      if (target == null) return;
      if (batching && !deferred.has(target)) deferred.set(target, target.deferNotifications());
      const errors: Thrown[] = [];
      ListenerErrors.isolate(errors, () => target.storeChanged(seen));
      ListenerErrors.report(listenerErrors, errors, "CellStoreView.link");
    });
    return () => {
      unsubChanges();
      unsubBatch();
      releaseAll();
    };
  }

}

/** A store change as the viewing cache sees it (see `StoreChange`), made inside an install bracket or not. */
function changeOf<K>(
  change: CellDataChange<K>,
  provenance: "install" | "write",
  bridgeActor: Actor,
  serverActor: Option<Actor>,
): StoreChange {
    const server = serverActor != null && change.actor === serverActor;
    const actor = server ? null : change.actor ?? bridgeActor;
    const install = provenance === "install";
    switch (change.kind) {
      case "set":
        return {
          kind: "date",
          range: change.entry.date.toRange(),
          date: change.entry.date,
          dtr: null,
          id: change.entry.id ?? null,
          value: change.entry.value,
          actor,
          install,
          server,
        };
      case "clear":
        return {
          kind: "date",
          range: change.date.toRange(),
          date: change.date,
          dtr: null,
          id: change.cellId ?? null,
          value: null,
          actor,
          install,
          server,
        };
      case "set-custom":
        return {
          kind: "timepoint",
          range: UtcDays.touched(change.entry.dtr),
          date: null,
          dtr: change.entry.dtr,
          id: change.entry.id,
          value: change.entry.value,
          actor,
          install,
          server,
        };
      case "clear-custom":
        return {
          kind: "timepoint",
          range: UtcDays.touched(change.dtr),
          date: null,
          dtr: change.dtr,
          id: change.cellId,
          value: null,
          actor,
          install,
          server,
        };
    }
}

/** Types that go with `CellStoreView`. */
export namespace CellStoreView {
  /** The write half of one row's view (see `CellStoreView`). */
  export interface Writes {
    set(date: NaiveDate.Partial, value: CellValue, id: Option<CellId>, actor: Option<Actor>): void;
    clear(date: NaiveDate.Partial, id: Option<CellId>, actor: Option<Actor>): void;
    setTimepoint(entry: Timepoint, actor: Option<Actor>): void;
    removeTimepoint(id: string, actor: Option<Actor>): void;
    replaceTimepoints(range: DateTime.Range<Utc>, entries: readonly Timepoint[], actor: Option<Actor>): void;
    batch<T>(fn: () => T): T;
    install(cells: readonly WireCell[], since: Option<number>): void;
    captureRead(): {
      readonly revision: number;
      readonly resident: object;
      readonly current: () => boolean;
      readonly release: Unsubscribe;
    };
    captureRevision(): number;
    releaseRevision(rev: number): void;
    writeAnswer(cells: readonly WireCell[], actor: Option<Actor>): void;
    adopt(dates: readonly CellEntry[], timepoints: readonly Timepoint[]): void;
  }
}

function idOf(id: Option<string>): Option<CellId> {
  return id == null ? null : CellId.of(id);
}

/** A wire cell as a timepoint entry (`null`: a date-keyed cell, or one without an id). */
function timepointOf(cell: WireCell): Option<Timepoint> {
  if (cell.id == null) return null;
  if (NaiveDate.Partial.parseOptional(cell.date) != null) return null;
  const parsed = DateTime.fromRfc3339(cell.date.toString());
  if (!parsed.isOk) return null;
  const start = parsed.exp("a timepoint cell's start").toUtc();
  const parsedEnd = cell.endDate == null ? null : DateTime.fromRfc3339(cell.endDate.toString());
  const end = parsedEnd != null && parsedEnd.isOk ? parsedEnd.exp("a timepoint cell's end").toUtc() : start;
  return { id: CellId.of(cell.id), dtr: new DateTime.Range(start, end), value: cell.data ?? null, style: null };
}

/**
 * Write one wire cell into `port` under `key`: a date-keyed cell by day (a
 * multi-cardinality one by its id; one without an id is left out), a
 * timepoint cell by id. `since`: leave a cell written locally after it.
 */
function writeOne<K>(
  port: CellStorePort<K>,
  key: K,
  cell: WireCell,
  since: Option<number>,
  dates: PartialDateMemo,
  cardinality: CellCardinality,
  actor: Option<Actor>,
): void {
  const value: CellValue = cell.data ?? null;
  const parsed = dates.parse(cell.date);
  if (parsed.isOk) {
    const date = parsed.exp("valid semantic cell date");
    const id = idOf(cell.id ?? null);
    if (cardinality === "multi" && id == null) return;
    if (since != null && port.writtenLocallySince(key, since, date, id)) return;
    port.set(key, date, value, id, null, actor);
    return;
  }
  const entry = timepointOf(cell);
  if (entry == null) return;
  if (since != null && port.customWrittenLocallySince(key, since, entry.id)) return;
  port.setCustom(key, entry, actor);
}
